import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router";
import Button from "../../../components/common/button/Button";
import Table from "../../../components/common/table/Table";
import { routeNames } from "../../../consts/routesNames";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import "./DisplayItems.css";
import { createMenuItem } from "../../../helpers/menuHelper";
import { useDispatch } from "react-redux";
import { openModal } from "../../../redux/modal/modalActions";
import ConfirmationBox from "../../../components/common/confirmationBox/ConfirmationBox";
import { navtabs } from "../../../consts/navTabs";
import { setActiveNavTab } from "../../../redux/navbar/navbarActions";
import { getAllItems } from "../../../apis/itemsApis/getAllItems";
import ResponseModal from "../../../components/common/modal/responseModal/ResponseModal";
import { deleteItem } from "../../../apis/itemsApis/deleteItem";
import { PERMISSIONS } from "../../../auth/utils/permissionCodes";
import { useCrudPermissions } from "../../../auth/hooks/useCrudPermissions";
import { useUpdateOwnership } from "../../../auth/hooks/useUpdateOwnership";
import { formatCreatedByDisplay } from "../../../utils/tableDisplayUtils";

function DisplayItems({ homePageDisplay }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    canCreate: canCreateItems,
    canUpdate: canUpdateItems,
    canDelete: canDeleteItems,
  } = useCrudPermissions(PERMISSIONS.ITEMS);
  const { canUpdateOwnedRecord } = useUpdateOwnership();

  const [itemsData, setItemsData] = useState([]);

  useEffect(() => {
    if (location.pathname === routeNames.ITEMS) {
      dispatch(setActiveNavTab(navtabs.ITEMS));
    }
  }, [location.pathname, dispatch]);

  // === Getting Table Data  ===
  const getItems = useCallback(async () => {
    try {
      const allItemRes = await getAllItems();
      if (allItemRes.error_code === 2 || allItemRes.error_code === 3) {
        dispatch(
          openModal(
            <ResponseModal text={allItemRes.error_message} isError={true} />
          )
        );
      } else {
        let itemsArray = [];
        if (allItemRes.res.data.length > 0) {
          itemsArray = allItemRes.res.data.map((item) => ({
            ...item,
            phase_name: item.phase.name,
            sub_phase_name: item.sub_phase.name,
            unit_name: item.unit.name,
            contact_supplier_name: item.supplier
              ? `${item.supplier.first_name ?? ""} ${item.supplier.last_name ?? ""}`.trim()
              : "---",
            company_supplier_name: item.company?.name ?? "---",
            created_by_display: formatCreatedByDisplay(item.created_by),
          }));
        }
        setItemsData(itemsArray);
      }
    } catch (error) {
      console.error("Error fetching items:", error);
    }
  }, [dispatch]);

  useEffect(() => {
    getItems();
  }, [getItems]);

  const tableHeaders = [
    {
      key: "name",
      name: "Name",
    },
    {
      key: "type",
      name: "Type",
    },
    {
      key: "phase_name",
      name: "Phase",
    },
    {
      key: "sub_phase_name",
      name: "Sub Phase",
    },
    {
      key: "contact_supplier_name",
      name: "Contact Supplier",
    },
    {
      key: "company_supplier_name",
      name: "Company Supplier",
    },
    {
      key: "unit_name",
      name: "Unit",
    },
    {
      key: "price",
      name: "Price",
    },
    {
      key: "created_by_display",
      name: "Created By",
    },
  ];
  // === Click Button Action Handlers ===
  const HandleItemDetailsClick = (rowData) => {
    navigate(routeNames.ITEM_DETAILS, { state: { data: rowData } });
  };

  const HandleAddNewItemClick = () => {
    if (!canCreateItems) return;
    navigate(routeNames.ADD_ITEM);
  };

  // === Delete Item Action ===
  const confirmDeleteItem = (rowData) => {
    if (!canDeleteItems) return;
    dispatch(
      openModal(
        <ConfirmationBox
          msg={`Are you sure you want to delete "${rowData.name}"`}
          saveText={"OK"}
          discardText={"Cancel"}
          saveAction={async () => {
            try {
              await deleteItem(rowData.id);
              errorDispatcher("Deleted Successfully");
              getItems();
            } catch (error) {
              console.error("Error deleting item:", error);
              errorDispatcher("Failed to delete the item.");
            }
          }}
        />
      )
    );
  };

  const errorDispatcher = (message) => {
    alert(message);
    dispatch(openModal(<ResponseModal text={message} isError={true} />));
  };

  // === Edit Item Action ===
  const canEditItem = (rowData) =>
    canUpdateOwnedRecord(rowData.created_by, canUpdateItems);

  const editItem = (rowData) => {
    if (!canEditItem(rowData)) return;
    navigate(`${routeNames.EDIT_ITEM.split(":")[0]}${rowData.id}`, {
      state: { data: rowData },
    });
  };

  const getItemRowActions = (row) =>
    [
      canEditItem(row) && createMenuItem("Edit", editItem, <EditIcon />),
      canDeleteItems && createMenuItem("Delete", confirmDeleteItem, <DeleteIcon />),
    ].filter(Boolean);

  return (
    <>
      <div
        className={
          homePageDisplay
            ? "main-container-displayItems-homePageDisplay"
            : "main-container-displayItems"
        }
      >
        {!homePageDisplay ? (
          <div className="btns-main-container-displayItems">
            {canCreateItems && (
              <div className="add-btn-container-displayItems">
                <Button
                  className={"add-btn-displayItems"}
                  buttonText={"Add New Item"}
                  onClick={HandleAddNewItemClick}
                />
              </div>
            )}
          </div>
        ) : (
          <></>
        )}

        <div className="table-main-container-displayItems">
          <Table
            tableHeight={homePageDisplay ? "450px" : "650px"}
            headers={tableHeaders}
            rows={itemsData}
            searchable={homePageDisplay ? false : true}
            onRowClick={HandleItemDetailsClick}
            getRowActions={getItemRowActions}
          />
        </div>
      </div>
    </>
  );
}

export default DisplayItems;
